import {
  Action,
  Module,
  Mutation,
  VuexModule,
  getModule,
} from 'vuex-module-decorators'
import store from '@/app/ui/store'
import {
  AdjustmentParcelPoin,
  DetailAdjustmentParcelPoin,
  HistoryLogPoinUser,
  ListAdjustmentParcelPoin,
  ListHistoryLogPoinUser,
} from '@/domain/entities/AdjustmentParcelPoin'
import {
  EnumTransactionParcelPoinRequest,
  EnumTypeBalanceStatus,
  EventBus,
  EventBusConstants,
  LocalStorage,
  REQUEST_TOP_SALDO_PAGINATION,
  Utils,
} from '@/app/infrastructures/misc'
import { AdjustmentParcelPoinPresenter } from '@/app/ui/presenters/AdjustmentParcelPoinPresenter'
import { container } from 'tsyringe'
import { Pagination } from '@/domain/entities/Pagination'
import Vue from 'vue'
import {
  AdjustmentParcelPoinApprovingPoinRequest,
  AdjustmentParcelPoinRequest,
} from '@/data/payload/api/AdjustmentParcelPoinRequest'

export interface AdjustmentParcelPoinState {
  isLoading: boolean
  listAdjustmentParcelPoin: AdjustmentParcelPoin[]
  paginationData: Pagination
  listHistoryLogPoinUser: HistoryLogPoinUser[]
  phoneNumber: string
  detailAdjustmentParcelPoin: DetailAdjustmentParcelPoin
}

export interface AdjustmentParcelPoinParamsInterface {
  page: string
  perPage: string
  phoneNumber?: string
}

export interface HistoryLogUserParamsInterface {
  page?: string | number
  perPage?: string | number
  customerId?: number
  sortBy?: string
}

export interface AdjustmentParcelPoinRequestState {
  customerId: number
  delta: number
  note: string
  pointType: EnumTransactionParcelPoinRequest
}

export interface AdjustmentParcelPoinApprovingState {
  pointId: number
  pointStatus: EnumTypeBalanceStatus
  reasonNote?: string
  username?: string
  actor?: string
}

@Module({
  namespaced: true,
  store,
  name: 'adjustment-parcel-poin',
  dynamic: true,
})
class AdjustmentParcelPoinController extends VuexModule
  implements AdjustmentParcelPoinState {
  private presenter: AdjustmentParcelPoinPresenter = container.resolve(
    AdjustmentParcelPoinPresenter
  )
  public isLoading = false
  public listAdjustmentParcelPoin: AdjustmentParcelPoin[] = [
    new AdjustmentParcelPoin(),
  ]
  public paginationData: Pagination = new Pagination(
    1,
    REQUEST_TOP_SALDO_PAGINATION,
    0
  )
  public listHistoryLogPoinUser: HistoryLogPoinUser[] = [
    new HistoryLogPoinUser(),
  ]
  public phoneNumber = ''
  public detailAdjustmentParcelPoin: DetailAdjustmentParcelPoin = new DetailAdjustmentParcelPoin()

  @Action({ rawError: true })
  public getAll(params: AdjustmentParcelPoinParamsInterface): void {
    this.setLoading(true)
    const formattedParams = Utils.toInstance(
      new Map(),
      JSON.stringify(params),
      'snake_case'
    )

    this.presenter
      .getAll(formattedParams)
      .then(res => {
        this.setListAdjustmentParcelPoin(res)
      })
      .catch(error => {
        Vue.notify({
          title: 'Fetch List Adjustment Poin Failed',
          text: [400, 422].includes(error.status)
            ? error.error.message.en
            : 'Something wrong',
          type: 'error',
          duration: 5000,
        })
        this.setListAdjustmentParcelPoin({
          data: [],
          pagination: {
            page: 1,
            perPage: REQUEST_TOP_SALDO_PAGINATION,
            totalItem: 0,
          },
        })
      })
      .finally(() => {
        this.setLoading(false)
      })
  }

  @Action({ rawError: true })
  public postRequestPoin(form: AdjustmentParcelPoinRequestState): void {
    this.setLoading(true)
    this.presenter
      .postRequestPoin(
        new AdjustmentParcelPoinRequest(
          form.customerId,
          form.delta,
          form.note,
          form.pointType
        )
      )
      .then(() => {
        EventBus.$emit(EventBusConstants.ADJUSTMENT_PARCEL_POIN_REQUEST_SUCCESS)
      })
      .catch(error => {
        Vue.notify({
          title: 'Request Adjustment Parcel Poin Failed',
          text:
            error.status === 400 || error.status === 422
              ? error.error.message.en
              : 'Something wrong',
          type: 'error',
          duration: 5000,
        })
      })
      .finally(() => {
        this.setLoading(false)
      })
  }

  @Action({ rawError: true })
  public getHistoryLogPoinUser(params: HistoryLogUserParamsInterface): void {
    this.setLoading(true)

    const formattedParams = Utils.toInstance(
      new Map(),
      JSON.stringify(params),
      'snake_case'
    )

    this.presenter
      .getHistoryLogPoinUser(formattedParams)
      .then((res: ListHistoryLogPoinUser) => {
        this.setListHistoryLogPoinUser(res)
      })
      .catch(error => {
        Vue.notify({
          title: 'Fetch List Adjustment Parcel Poin Failed',
          text: [400, 422].includes(error.status)
            ? error.error.message.en
            : 'Something wrong',
          type: 'error',
          duration: 5000,
        })
        this.setListHistoryLogPoinUser({
          data: [],
          pagination: {
            page: 1,
            perPage: REQUEST_TOP_SALDO_PAGINATION,
            totalItem: 0,
          },
        })
      })
      .finally(() => {
        this.setLoading(false)
      })
  }

  @Action({ rawError: true })
  public getDetailAdjustmentParcelPoin(id: number): void {
    this.setLoading(true)
    this.presenter
      .getDetail(id)
      .then((result: DetailAdjustmentParcelPoin) => {
        this.setDetailAdjustmentParcelPoin(result)
      })
      .catch(error => {
        Vue.notify({
          title: 'Fetch Detail Adjustment Parcel Poin Failed',
          text: [400, 422].includes(error.status)
            ? error.error.message.en
            : 'Something wrong',
          type: 'error',
          duration: 5000,
        })
        this.setDetailAdjustmentParcelPoin({
        })
      })
  }

  @Action({ rawError: true })
  public postApprovingPoin(form: AdjustmentParcelPoinApprovingState): void {
    this.setLoading(true)

    const actor = LocalStorage.getLocalStorage(
      LocalStorage.LOGIN_IDENTITY,
      true
    )

    this.presenter
      .postApproving(
        new AdjustmentParcelPoinApprovingPoinRequest(
          form.pointId,
          form.pointStatus,
          form.reasonNote,
          actor,
          actor
        )
      )
      .then(() => {
        if (form.pointStatus === EnumTypeBalanceStatus.APPROVED) {
          EventBus.$emit(
            EventBusConstants.ADJUSTMENT_PARCEL_POIN_APPROVING_APPROVE_SUCCESS
          )
        } else {
          EventBus.$emit(
            EventBusConstants.ADJUSTMENT_PARCEL_POIN_APPROVING_REJECT_SUCCESS
          )
        }
      })
      .catch(error => {
        if (form.pointStatus === EnumTypeBalanceStatus.APPROVED) {
          EventBus.$emit(
            EventBusConstants.ADJUSTMENT_PARCEL_POIN_APPROVING_APPROVE_FAILED
          )
        } else {
          EventBus.$emit(
            EventBusConstants.ADJUSTMENT_PARCEL_POIN_APPROVING_REJECT_FAILED
          )
        }
        Vue.notify({
          title: `Approving Parcel Poin Failed To ${Utils.toCapitalize(
            form.pointStatus
          )}`,
          text:
            error.status === 400 || error.status === 422
              ? error.error.message.en
              : 'Something wrong',
          type: 'error',
          duration: 5000,
        })
      })
      .finally(() => {
        this.setLoading(false)
      })
  }

  @Mutation
  private setLoading(bool: boolean): void {
    this.isLoading = bool
  }

  @Mutation
  private setListAdjustmentParcelPoin(res: ListAdjustmentParcelPoin): void {
    this.listAdjustmentParcelPoin = <AdjustmentParcelPoin[]>res.data
    this.paginationData = <Pagination>res.pagination
  }

  @Mutation
  private setListHistoryLogPoinUser(res: ListHistoryLogPoinUser): void {
    this.listHistoryLogPoinUser = <HistoryLogPoinUser[]>res.data
    this.phoneNumber = res.phoneNumber || ''
    this.paginationData = <Pagination>res.pagination
  }

  @Mutation
  private setDetailAdjustmentParcelPoin(res: DetailAdjustmentParcelPoin): void {
    this.detailAdjustmentParcelPoin = <DetailAdjustmentParcelPoin>res
  }
}

export default getModule(AdjustmentParcelPoinController)
